import {
    ASX_IHub_AppFeedback_FormConfig,
    useFeedbackForm,
    useCooldown,
    FeedbackFormSidePanel,
} from "@amzn/ads-feedback-kit";
import { ThemeProvider } from "@amzn/storm-ui";
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { Paths } from "../../constants/paths";
import { TELEMETRY_ID } from "../../constants/telemetry";
import { insightDetailComponentStore } from "../../store/insightDetailComponent";
import { insightDetailsStore } from "../../store/insightDetails";
import { isTestSiteOrDev } from "../../utils/environment.utils";
import { logWithTakt } from "../../utils/telemetry.utils";

import { AppCsatFeedbackFormRules } from "./AppCsatHelper";

const StyledAppCsatContainer = styled.div`
    position: absolute;
    z-index: 999;
    top: 5px;
`;

export const AppCsatFeedback = (): JSX.Element => {
    const location = useLocation();
    const isDefaultRoute =
        location.pathname === Paths.DISCOVERY.toString() ||
        location.pathname === Paths.HOME.toString();
    const [isInsightDetailsOpen] = useStore(insightDetailsStore, (state) => [
        state.open,
    ]);
    const [isInsightDetailComponentOpen] = useStore(
        insightDetailComponentStore,
        (state) => [state.open],
    );
    const isInsightSidePanelOpen =
        isInsightDetailsOpen || isInsightDetailComponentOpen;
    const {
        checkCooldownIsExpired,
        restartSubmissionCooldown,
        restartCancellationCooldown,
    } = useCooldown({
        surveyName: ASX_IHub_AppFeedback_FormConfig.surveyContext.surveyName,
        surveyGroup: "asx-ihub-app",
        afterCancellation: 30,
        afterSubmission: 90,
        durationUnit: "day",
        adsWideCooldownOptOut: true,
        sessionCooldownOptOut: true,
    });
    const { openFeedbackForm, triggerTaktAttributes, isEligibleForFeedback } =
        useFeedbackForm({ formConfig: ASX_IHub_AppFeedback_FormConfig });
    const [showFeedbackView, setShowFeedbackView] = useState(false);
    const [userEngagement, setUserEngagement] = useState({
        clicks: 0,
        scrolls: 0,
        timeSpent: 0,
    });
    const sessionStartTime = useRef(Date.now());
    const isDirectAccess = useRef(
        !document.referrer ||
            document.referrer.includes(window.location.hostname),
    );

    useEffect(() => {
        const trackEngagement = () => {
            setUserEngagement((prev) => ({ ...prev, clicks: prev.clicks + 1 }));
        };
        const trackScroll = () => {
            setUserEngagement((prev) => ({
                ...prev,
                scrolls: prev.scrolls + 1,
            }));
        };

        document.addEventListener("click", trackEngagement);
        document.addEventListener("scroll", trackScroll);

        const timer = setInterval(() => {
            setUserEngagement((prev) => ({
                ...prev,
                timeSpent: Date.now() - sessionStartTime.current,
            }));
        }, 5000);

        return () => {
            document.removeEventListener("click", trackEngagement);
            document.removeEventListener("scroll", trackScroll);
            clearInterval(timer);
        };
    }, []);

    useEffect(() => {
        if (
            !isTestSiteOrDev() &&
            isDirectAccess.current &&
            !isInsightSidePanelOpen &&
            isDefaultRoute &&
            checkCooldownIsExpired()
        ) {
            const hasMinEngagement =
                userEngagement.timeSpent >= 30000 &&
                (userEngagement.clicks >= 3 || userEngagement.scrolls >= 2);

            if (hasMinEngagement) {
                setShowFeedbackView(true);
                openFeedbackForm();
            }
        }
    }, [
        checkCooldownIsExpired,
        openFeedbackForm,
        triggerTaktAttributes,
        userEngagement,
        isInsightSidePanelOpen,
        isDefaultRoute,
    ]);

    const handleClose = () => {
        restartCancellationCooldown();
        logWithTakt(TELEMETRY_ID.CSAT_APP_FEEDBACK_CANCELLED);
        setShowFeedbackView(false);
    };

    const handleSubmit = () => {
        restartSubmissionCooldown();
        logWithTakt(TELEMETRY_ID.CSAT_APP_FEEDBACK_SUBMITTED);
        setShowFeedbackView(false);
    };

    return (
        <ThemeProvider>
            {showFeedbackView && isEligibleForFeedback && (
                <StyledAppCsatContainer>
                    <FeedbackFormSidePanel
                        formConfig={ASX_IHub_AppFeedback_FormConfig}
                        locale={"en-US"}
                        rules={AppCsatFeedbackFormRules}
                        allowMultiple={false}
                        onClose={handleClose}
                        onSubmit={handleSubmit}
                        side="end"
                    />
                </StyledAppCsatContainer>
            )}
        </ThemeProvider>
    );
};
